/**
 * Meta (Facebook) Pixel for HomeSmart NEXT.
 *
 * The base code is injected on mount and fires PageView. `track()` in
 * utils/tracking.ts forwards each landing page event to the pixel, including
 * `Lead` on form submission.
 */
export const META_PIXEL_ID = '959873449860294'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[][]
  push?: unknown
  loaded?: boolean
  version?: string
}

interface PixelWindow extends Window {
  fbq?: Fbq
  _fbq?: Fbq
}

export function isPixelConfigured(): boolean {
  return META_PIXEL_ID.trim().length > 0
}

/** Adds the no-script fallback tracking pixel once. */
function appendNoScriptFallback(): void {
  if (document.getElementById('meta-pixel-noscript')) return

  const noscript = document.createElement('noscript')
  noscript.id = 'meta-pixel-noscript'
  const img = document.createElement('img')
  img.height = 1
  img.width = 1
  img.alt = ''
  img.style.display = 'none'
  img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`
  noscript.appendChild(img)
  document.body.appendChild(noscript)
}

/** Injects the pixel base code once and fires PageView. */
export function initMetaPixel(): void {
  if (!isPixelConfigured()) return

  const w = window as PixelWindow
  if (w.fbq) {
    w.fbq('track', 'PageView')
    return
  }

  const fbq: Fbq = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue?.push(args)
    }
  } as Fbq

  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  w.fbq = fbq
  w._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  fbq('init', META_PIXEL_ID)
  fbq('track', 'PageView')
  appendNoScriptFallback()
}

/** Fires a standard pixel event (Lead, Schedule, Contact, ...). */
export function pixelStandard(event: string, payload: Record<string, unknown> = {}): void {
  if (!isPixelConfigured()) return
  ;(window as PixelWindow).fbq?.('track', event, payload)
}

/** Fires a custom pixel event for anything without a standard equivalent. */
export function pixelCustom(event: string, payload: Record<string, unknown> = {}): void {
  if (!isPixelConfigured()) return
  ;(window as PixelWindow).fbq?.('trackCustom', event, payload)
}
